import { TrendingUp, TrendingDown, Star, Shield, Zap, Bot } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../../ui/card";
import { DashboardInsights, PERIOD_LABELS, type PeriodDays } from "../../../../../../hooks/useDashboardInsights";
import { Skeleton } from "../../../../../ui/skeleton";
import { DataLabel, NoData } from "./DataLabel";
import { TooltipInfo } from "./TooltipInfo";

const METRIC_TOOLTIPS = {
  customers: {
    title: "What this means",
    description: "Number of new reviews received this week.\n\nMore reviews increase your visibility and credibility on platforms like Google.",
  },
  rating: {
    title: "What this means",
    description: "This shows the change in your average rating compared to last week.\n\nIt is calculated as:\nCurrent rating − Previous rating\n\nExample:\n4.8 → 4.3 = −0.5⭐",
  },
  reputation: {
    title: "What this means",
    description: "These are low-rating (1–3⭐) reviews captured through EpicReview before being posted publicly.\n\nThis helps protect your public rating and brand perception.",
  },
  ratingImpact: {
    title: "What this means",
    description: "This shows how much your rating was preserved by preventing negative reviews from going public.\n\nIt is calculated by simulating what your rating would be if those reviews were included.",
  },
  aiResponse: {
    title: "What this means",
    description: "Percentage of reviews that have been responded to using AI.\n\nResponding to reviews improves customer trust and SEO ranking.",
  },
} as const;

interface GrowthImpactRowProps {
  insights: DashboardInsights | null;
  loading: boolean;
  periodDays: PeriodDays;
}

const TrendBadge = ({ value, suffix = "", periodLabel }: { value: number; suffix?: string; periodLabel: string }) => {
  if (value === 0) return <span className="text-xs text-muted-foreground">No change</span>;
  const positive = value > 0;
  return (
    <span className={`inline-flex items-center gap-0.5 text-xs font-medium ${positive ? "text-emerald-600" : "text-red-500"}`}>
      {positive ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
      {positive ? "+" : ""}{value}{suffix} vs {periodLabel}
    </span>
  );
};

export const GrowthImpactRow = ({ insights, loading, periodDays }: GrowthImpactRowProps) => {
  const labels = PERIOD_LABELS[periodDays];
  if (loading) {
    return (
      <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
        {Array.from({ length: 5 }).map((_, i) => (
          <Card key={i} className="border-border/50">
            <CardHeader className="pb-2"><Skeleton className="h-4 w-24" /></CardHeader>
            <CardContent><Skeleton className="h-8 w-16" /></CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (!insights) return <NoData />;

  if (insights.total_reviews === 0) {
    return (
      <Card className="border-border/50 p-6 text-center">
        <NoData message="No reviews yet — start syncing to see growth metrics." />
      </Card>
    );
  }

  const cards = [
    {
      title: "Customers Gained",
      value: `+${Math.max(insights.review_growth, 0)}`,
      sub: <TrendBadge value={insights.review_growth_percent} suffix="%" />,
      icon: TrendingUp,
      color: "text-emerald-600",
      dataType: "derived" as const,
      tooltip: METRIC_TOOLTIPS.customers,
    },
    {
      title: "Rating Movement",
      value: insights.rating_delta === 0
        ? "0.0 ⭐"
        : `${insights.rating_delta > 0 ? "+" : ""}${insights.rating_delta} ⭐ ${insights.rating_delta > 0 ? "↑" : "↓"}`,
      sub: <span className="text-xs text-muted-foreground">{insights.current_avg_rating}⭐ this week</span>,
      icon: Star,
      color: insights.rating_delta > 0 ? "text-emerald-600" : insights.rating_delta < 0 ? "text-red-500" : "text-muted-foreground",
      dataType: "derived" as const,
      tooltip: METRIC_TOOLTIPS.rating,
    },
    {
      title: "Reputation Protected",
      value: insights.intercepted_reviews.toString(),
      sub: <span className="text-xs text-muted-foreground">low-rating reviews flagged</span>,
      icon: Shield,
      color: "text-amber-500",
      dataType: "derived" as const,
      tooltip: METRIC_TOOLTIPS.reputation,
    },
    {
      title: "Rating Impact",
      value: `+${insights.rating_preserved} ⭐`,
      sub: <span className="text-xs text-muted-foreground">preserved through management</span>,
      icon: Zap,
      color: "text-blue-500",
      dataType: "estimated" as const,
      tooltip: METRIC_TOOLTIPS.ratingImpact,
    },
    {
      title: "AI Response Rate",
      value: `${insights.response_rate}%`,
      sub: <span className="text-xs text-muted-foreground">{insights.handled_count}/{insights.needs_attention_count} managed</span>,
      icon: Bot,
      color: "text-violet-500",
      dataType: "derived" as const,
      tooltip: METRIC_TOOLTIPS.aiResponse,
    },
  ];

  return (
    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-5">
      {cards.map((card) => (
        <Card key={card.title} className="border-border/50 transition-shadow hover:shadow-md">
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium text-muted-foreground flex items-center gap-1">
              {card.title}
              {card.tooltip && <TooltipInfo title={card.tooltip.title} description={card.tooltip.description} />}
            </CardTitle>
            <card.icon className={`h-4 w-4 ${card.color}`} />
          </CardHeader>
          <CardContent>
            <div className={`text-2xl font-bold ${card.color}`}>{card.value}</div>
            <div className="mt-1">{card.sub}</div>
            <DataLabel type={card.dataType} className="mt-2" />
          </CardContent>
        </Card>
      ))}
    </div>
  );
};
